/**
 * Admin Books (Analytics & Governance) Console
 * 
 * BOOK INTELLIGENCE CONSOLE
 * 
 * PURPOSE: Admin-only, read-only governance analytics for Books (Manuscripts),
 * enabling insight into book ownership, engagement performance, and operational health.
 * 
 * STRATEGIC REPOSITIONING:
 * - This is NO LONGER a BSP pipeline dashboard
 * - This IS a Book Intelligence Console answering:
 *   - What books exist?
 *   - Who owns them (Independent Author / Managed Author / Publisher)?
 *   - How are they performing with Readers?
 *   - Which titles matter operationally right now?
 * 
 * DATA SOURCES:
 * - reader_credit_ledger = RC Consumed (economic truth)
 * - sessions = Playback Sessions
 * - audia_session_summaries = Ask Audia Sessions
 * - manuscripts = Book identity and metadata
 * - bsp_status / manuscript BSP fields = Operational health (secondary)
 * 
 * DESIGN PRINCIPLES:
 * 1. Admin-Only, Read-Only (Pre-MVP)
 * 2. Engagement ≠ Earnings - explicit labeling
 * 3. Reader-Only Economic Activity
 * 4. Date-bounded queries (default: 30 days)
 * 5. BSP is operational metadata, NOT the identity of the book
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import AdminNav from '../components/AdminNav';
import AdminTopMenuBar from '../components/AdminTopMenuBar';
import AdminMobileBottomNav from '../components/AdminMobileBottomNav';
import UniversalFooter from '../components/UniversalFooter';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  BookOpen,
  Users,
  Clock,
  CreditCard,
  MessageSquare,
  TrendingUp,
  Search,
  RefreshCw,
  AlertCircle,
  Loader2,
  Calendar,
  Activity,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Building2,
  User,
  Filter,
  ArrowUpDown,
  X,
  Brain,
  ChevronUp,
  ChevronDown,
  Trash2,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

// API_URL imported from centralized config

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const formatNumber = (num) => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num?.toLocaleString() || '0';
};

const formatRC = (rc) => {
  return `${rc?.toFixed(2) || '0.00'} RC`;
};

const formatSeconds = (seconds) => {
  if (!seconds || seconds === 0) return '0m';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

// Non-Payout Disclaimer Banner
const EngagementDisclaimer = () => (
  <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-3 mb-6" data-testid="engagement-disclaimer">
    <div className="flex items-start gap-2">
      <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
      <div>
        <p className="text-xs text-amber-300 font-medium">
          Engagement / Consumption Metrics (Non-Payout)
        </p>
        <p className="text-xs text-amber-400/80 mt-0.5">
          These figures represent Reader activity and content usage signals. They do not represent earnings, revenue share, or payout obligations.
        </p>
      </div>
    </div>
  </div>
);

// Summary KPI Card
const KPICard = ({ icon: Icon, label, value, subValue, color = "blue", tooltip }) => {
  const colorClasses = {
    blue: "bg-blue-500/20 text-blue-400",
    green: "bg-green-500/20 text-green-400",
    purple: "bg-purple-500/20 text-purple-400",
    amber: "bg-amber-500/20 text-amber-400",
    cyan: "bg-cyan-500/20 text-cyan-400",
    pink: "bg-pink-500/20 text-pink-400",
    emerald: "bg-emerald-500/20 text-emerald-400",
    red: "bg-red-500/20 text-red-400"
  };

  return (
    <Card className="bg-slate-900/60 border-slate-700" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-slate-400 truncate" title={tooltip}>{label}</p>
            <p className="text-xl font-bold text-white">{value}</p>
            {subValue && <p className="text-xs text-slate-500">{subValue}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// BSP Status Badge
const BSPStatusBadge = ({ status }) => {
  const getConfig = () => {
    switch (status) {
      case 'ready':
        return { icon: CheckCircle, label: 'Ready', className: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/50' };
      case 'generating':
        return { icon: Loader2, label: 'Generating', className: 'bg-blue-500/20 text-blue-300 border-blue-500/50', animate: true };
      case 'issue':
        return { icon: XCircle, label: 'Issue', className: 'bg-red-500/20 text-red-300 border-red-500/50' };
      default:
        return { icon: AlertTriangle, label: 'None', className: 'bg-slate-500/20 text-slate-400 border-slate-500/50' };
    }
  };

  const config = getConfig();
  const Icon = config.icon;

  return (
    <Badge className={`text-xs inline-flex items-center gap-1 ${config.className}`}>
      <Icon className={`w-3 h-3 ${config.animate ? 'animate-spin' : ''}`} />
      {config.label}
    </Badge>
  );
};

// Sortable Table Header
const SortableHeader = ({ column, label, currentSort, currentDir, onSort, align = 'left' }) => {
  const isActive = currentSort === column;
  
  return (
    <TableHead 
      className={`text-slate-400 cursor-pointer hover:text-white transition-colors select-none ${align === 'right' ? 'text-right' : ''}`}
      onClick={() => onSort(column)}
    >
      <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : ''}`}>
        <span>{label}</span>
        <div className="flex flex-col">
          <ChevronUp 
            className={`w-3 h-3 -mb-1 ${isActive && currentDir === 'asc' ? 'text-white' : 'text-slate-600'}`} 
          />
          <ChevronDown 
            className={`w-3 h-3 -mt-1 ${isActive && currentDir === 'desc' ? 'text-white' : 'text-slate-600'}`} 
          />
        </div>
      </div>
    </TableHead>
  );
};

import BSPDetailModal from '../components/BSPDetailModal';

// Book Analytics Drawer
const BookAnalyticsDrawer = ({ book, isOpen, onClose, dateFrom, dateTo, onViewBsp }) => {
  const { accessToken: token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    if (isOpen && book?.book_id) {
      fetchAnalytics();
    }
  }, [isOpen, book?.book_id]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);

      const response = await fetch(`${API_URL}/api/admin/books/${book.book_id}/analytics?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch book analytics');
      
      const data = await response.json();
      setAnalytics(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load book analytics');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg bg-slate-900 border-slate-700 overflow-y-auto" data-testid="book-analytics-drawer">
        <SheetHeader className="mb-6">
          <SheetTitle className="text-xl text-white flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-blue-400" />
            Book Analytics
          </SheetTitle>
          <SheetDescription className="text-slate-400">
            {book?.title || 'Loading...'}
          </SheetDescription>
        </SheetHeader>

        <EngagementDisclaimer />

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : analytics ? (
          <div className="space-y-6">
            {/* Book Cover and Identity */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-300">Book Identity</h3>
              <div className="bg-slate-800/50 rounded-lg p-4">
                <div className="flex gap-4">
                  {/* Book Cover */}
                  <div className="flex-shrink-0">
                    <img
                      src={`${API_URL}/api/covers/${analytics.book_id}?v=${analytics.cover_updated_at || Date.now()}`}
                      alt={analytics.title}
                      className="w-24 h-36 rounded-lg object-cover shadow-lg ring-1 ring-slate-700"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div 
                      className="hidden w-24 h-36 rounded-lg bg-slate-700 items-center justify-center"
                    >
                      <BookOpen className="w-8 h-8 text-slate-500" />
                    </div>
                  </div>
                  {/* Book Info */}
                  <div className="flex-1 min-w-0 space-y-2">
                    <div>
                      <p className="text-lg font-bold text-white">{analytics.title}</p>
                      {analytics.subtitle && <p className="text-sm text-slate-400">{analytics.subtitle}</p>}
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <User className="w-4 h-4 text-slate-500" />
                      <span className="text-slate-300">{analytics.author}</span>
                    </div>
                    {analytics.publisher_name && (
                      <div className="flex items-center gap-2 text-sm">
                        <Building2 className="w-4 h-4 text-slate-500" />
                        <span className="text-slate-400">{analytics.publisher_name}</span>
                        {analytics.managed_author_name && (
                          <span className="text-slate-500">via {analytics.managed_author_name}</span>
                        )}
                      </div>
                    )}
                    {!analytics.publisher_name && (
                      <div className="flex items-center gap-2 text-sm">
                        <User className="w-4 h-4 text-blue-400" />
                        <span className="text-blue-400">Independent Author</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Engagement Metrics */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-300">Engagement Summary</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <p className="text-xs text-slate-500">RC Consumed</p>
                  <p className="text-lg font-bold text-amber-400">{formatRC(analytics.rc_consumed)}</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <p className="text-xs text-slate-500">Playback Time</p>
                  <p className="text-lg font-bold text-cyan-400">{formatSeconds(analytics.total_playback_seconds)}</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <p className="text-xs text-slate-500">Unique Readers</p>
                  <p className="text-lg font-bold text-green-400">{formatNumber(analytics.total_readers)}</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <p className="text-xs text-slate-500">Sessions</p>
                  <p className="text-lg font-bold text-blue-400">{formatNumber(analytics.playback_sessions)}</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <p className="text-xs text-slate-500">Avg. Engagement</p>
                  <p className="text-lg font-bold text-purple-400">{formatSeconds(analytics.avg_engagement_seconds)}</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <p className="text-xs text-slate-500">Ask Audia</p>
                  <p className="text-lg font-bold text-pink-400">{formatNumber(analytics.ask_audia_sessions)}</p>
                </div>
              </div>
            </div>

            {/* Completion Rate */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-300">Completion</h3>
              <div className="bg-slate-800/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-400">Avg. Completion Rate</span>
                  <span className="text-lg font-bold text-white">{analytics.completion_rate}%</span>
                </div>
                <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all"
                    style={{ width: `${analytics.completion_rate}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Book Metadata */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-300">Book Metadata</h3>
              <div className="bg-slate-800/50 rounded-lg p-4 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-slate-500">Genre</p>
                  <p className="text-slate-300">{analytics.genre || '—'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Theme</p>
                  <p className="text-slate-300">{analytics.theme || '—'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Word Count</p>
                  <p className="text-slate-300">{analytics.word_count?.toLocaleString() || '—'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Initial TTS Cost</p>
                  <p className="text-emerald-400 font-medium">{analytics.initial_tts_cost > 0 ? `$${analytics.initial_tts_cost.toFixed(4)}` : '—'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Upload Date</p>
                  <p className="text-slate-300">{formatDate(analytics.upload_date)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Edit TTS Cost</p>
                  <p className="text-amber-400 font-medium">{analytics.edit_tts_cost > 0 ? `$${analytics.edit_tts_cost.toFixed(4)}` : '—'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Age</p>
                  <p className="text-slate-300">{analytics.age_days} days</p>
                </div>
              </div>
            </div>

            {/* BSP Status */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-300">Operational Health (BSP)</h3>
              <div className="bg-slate-800/50 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-400">BSP Status</span>
                  <BSPStatusBadge status={analytics.bsp_status} />
                </div>
                {analytics.bsp_version && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">Version</span>
                    <span className="text-sm text-slate-300">{analytics.bsp_version}</span>
                  </div>
                )}
                {analytics.bsp_generated_at && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">Generated</span>
                    <span className="text-sm text-slate-300">{formatDate(analytics.bsp_generated_at)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-400">Has Summary</span>
                  <span className="text-sm text-slate-300">{analytics.bsp_has_summary ? 'Yes' : 'No'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-400">Has Themes</span>
                  <span className="text-sm text-slate-300">{analytics.bsp_has_themes ? 'Yes' : 'No'}</span>
                </div>
                {/* View Full BSP Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onViewBsp && onViewBsp(book)}
                  className="w-full mt-3 text-purple-400 border-purple-500/50 hover:bg-purple-500/10 font-semibold"
                  data-testid="view-bsp-from-drawer"
                >
                  <Brain className="w-4 h-4 mr-2" />
                  View Full BSP Profile
                </Button>
              </div>
            </div>

            {/* Period Info */}
            <div className="text-xs text-slate-500 text-center pt-4 border-t border-slate-700">
              Data period: {formatDate(analytics.period_start)} – {formatDate(analytics.period_end)}
            </div>
          </div>
        ) : (
          <div className="text-center py-12 text-slate-400">
            No analytics data available
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const AdminBooksPage = () => {
  const { accessToken: token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Data state
  const [loading, setLoading] = useState(true);
  const [books, setBooks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [totalBooks, setTotalBooks] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  
  // Drawer state
  const [selectedBook, setSelectedBook] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  
  // BSP Modal state
  const [bspModalBook, setBspModalBook] = useState(null);
  const [showBspModal, setShowBspModal] = useState(false);
  
  // Filters from URL
  const page = parseInt(searchParams.get('page') || '1');
  const pageSize = parseInt(searchParams.get('pageSize') || '20');
  const search = searchParams.get('search') || '';
  const dateRange = searchParams.get('dateRange') || '30';
  const sortBy = searchParams.get('sortBy') || 'title';
  const sortDir = searchParams.get('sortDir') || 'asc';
  
  // Delete book state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteStep, setDeleteStep] = useState(0); // 0: none, 1: first warning, 2: final confirm
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [deletedTitle, setDeletedTitle] = useState('');

  // Calculate date range
  const getDateParams = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - parseInt(dateRange));
    return {
      date_from: startDate.toISOString(),
      date_to: endDate.toISOString()
    };
  };

  // Update URL params
  const updateParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        newParams.set(key, String(value));
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  };

  // Fetch books list
  const fetchBooks = useCallback(async () => {
    try {
      setLoading(true);
      const dates = getDateParams();
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        date_from: dates.date_from,
        date_to: dates.date_to,
        sort_by: sortBy,
        sort_dir: sortDir,
        ...(search && { search })
      });

      const response = await fetch(`${API_URL}/api/admin/books?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch books');
      
      const data = await response.json();
      setBooks(data.books || []);
      setTotalBooks(data.total || 0);
      setHasMore(data.has_more || false);
      setSummary(data.summary || null);
    } catch (error) {
      console.error('Error fetching books:', error);
      toast.error('Failed to load books');
    } finally {
      setLoading(false);
    }
  }, [token, page, pageSize, search, dateRange, sortBy, sortDir]);

  // Initial load and refresh on filter change
  useEffect(() => {
    fetchBooks();
  }, [fetchBooks]);

  // Refresh handler
  const handleRefresh = () => {
    fetchBooks();
    toast.success('Data refreshed');
  };

  // Handle row click
  const handleViewBook = (book) => {
    setSelectedBook(book);
    setShowDrawer(true);
  };

  // Handle delete book - nuclear option with double confirmation
  const handleDeleteClick = (e, book) => {
    e.stopPropagation();
    setDeleteTarget(book);
    setDeleteStep(1);
  };

  const handleDeleteConfirm = async () => {
    if (deleteStep === 1) {
      setDeleteStep(2);
      return;
    }
    
    // Final delete
    setIsDeleting(true);
    const titleToDelete = deleteTarget?.title || 'Unknown';
    
    try {
      console.log('[DELETE] Initiating nuclear delete for:', deleteTarget?.book_id);
      const response = await fetch(`${API_URL}/api/admin/books/${deleteTarget.book_id}/nuclear-delete`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('[DELETE] Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('[DELETE] Success:', data);
        
        // Close the confirmation modal
        setDeleteTarget(null);
        setDeleteStep(0);
        
        // Show success modal
        setDeletedTitle(titleToDelete);
        setShowDeleteSuccess(true);
        
        // Refresh the books list
        fetchBooks();
      } else {
        const error = await response.json().catch(() => ({}));
        console.error('[DELETE] Failed:', error);
        toast.error(error.detail || 'Failed to delete book');
      }
    } catch (err) {
      console.error('[DELETE] Error:', err);
      toast.error('Failed to delete book: ' + err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteTarget(null);
    setDeleteStep(0);
  };

  // Handle column header click for sorting
  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle direction if same column
      updateParams({ sortDir: sortDir === 'asc' ? 'desc' : 'asc', page: '1' });
    } else {
      // New column, default to descending
      updateParams({ sortBy: column, sortDir: 'desc', page: '1' });
    }
  };

  // Handle View BSP button click
  const handleViewBsp = (e, book) => {
    e.stopPropagation(); // Prevent row click from triggering
    setBspModalBook(book);
    setShowBspModal(true);
  };

  const totalPages = Math.ceil(totalBooks / pageSize);
  const dates = getDateParams();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      <AdminNav />
      <AdminTopMenuBar />
      
      <div className="pb-20 md:pb-0">
        <main className="container mx-auto px-4 py-8 max-w-7xl">
          {/* Header */}
          <div className="mb-6" data-testid="books-console-header">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-white mb-1">
                  Book Intelligence Console
                </h1>
                <p className="text-slate-400">
                  Governance analytics for book ownership, engagement, and operational health.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Select value={dateRange} onValueChange={(v) => updateParams({ dateRange: v, page: '1' })}>
                  <SelectTrigger className="w-[150px] bg-slate-800 border-slate-700" data-testid="date-range-select">
                    <Calendar className="w-4 h-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">Last 7 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                    <SelectItem value="90">Last 90 days</SelectItem>
                    <SelectItem value="365">Last year</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={handleRefresh} variant="outline" size="sm" data-testid="refresh-button">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </div>
          </div>

          {/* Context Banner */}
          <EngagementDisclaimer />

          {/* Summary KPIs - 2 rows of 4 for better readability */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="summary-kpis">
              <KPICard 
                icon={BookOpen} 
                label="Total Books" 
                value={formatNumber(summary.total_books)}
                color="blue"
              />
              <KPICard 
                icon={Activity} 
                label="Active Books" 
                value={formatNumber(summary.active_books)}
                subValue="≥1 session in period"
                color="green"
              />
              <KPICard 
                icon={Users} 
                label="Unique Readers" 
                value={formatNumber(summary.total_readers)}
                color="purple"
              />
              <KPICard 
                icon={CreditCard} 
                label="RC Consumed" 
                value={formatRC(summary.total_rc_consumed)}
                color="amber"
              />
              <KPICard 
                icon={Clock} 
                label="Playback Time" 
                value={formatSeconds(summary.total_playback_seconds)}
                color="cyan"
              />
              <KPICard 
                icon={TrendingUp} 
                label="Avg. Engagement" 
                value={formatSeconds(summary.avg_engagement_seconds)}
                color="pink"
              />
              <KPICard 
                icon={CheckCircle} 
                label="BSP Ready" 
                value={formatNumber(summary.bsp_ready_count)}
                color="emerald"
              />
              <KPICard 
                icon={XCircle} 
                label="BSP Issues" 
                value={formatNumber(summary.bsp_issues_count)}
                color="red"
              />
            </div>
          )}

          {/* Filters */}
          <Card className="bg-slate-900/60 border-slate-700 mb-6" data-testid="filters-card">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
                {/* Search */}
                <div className="flex-1 w-full md:max-w-md">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search title, author..."
                      value={search}
                      onChange={(e) => updateParams({ search: e.target.value, page: '1' })}
                      className="pl-10 bg-slate-800 border-slate-700"
                      data-testid="search-input"
                    />
                  </div>
                </div>

                {/* Clear Search */}
                {search && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => updateParams({ search: '', page: '1' })}
                    className="text-white font-semibold hover:text-white hover:bg-slate-800"
                    data-testid="clear-search"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Books Table */}
          <Card className="bg-slate-900/60 border-slate-700" data-testid="books-table-card">
            <CardHeader>
              <CardTitle className="text-white">Books</CardTitle>
              <CardDescription>
                Click on a book to view detailed analytics. Showing {books.length} of {totalBooks} books.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : books.length === 0 ? (
                <div className="text-center py-12 text-slate-400" data-testid="no-books-message">
                  {search ? 'No books match your search.' : 'No books found.'}
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <Table data-testid="books-table">
                      <TableHeader>
                        <TableRow className="border-slate-700">
                          <SortableHeader column="title" label="Title" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} />
                          <SortableHeader column="author" label="Author" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} />
                          <SortableHeader column="publisher" label="Publisher" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} />
                          <SortableHeader column="readers" label="Readers" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} align="right" />
                          <SortableHeader column="rc_consumed" label="RC Consumed" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} align="right" />
                          <SortableHeader column="playback_time" label="Playback" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} align="right" />
                          <SortableHeader column="engagement" label="Avg. Eng." currentSort={sortBy} currentDir={sortDir} onSort={handleSort} align="right" />
                          <SortableHeader column="last_activity" label="Last Activity" currentSort={sortBy} currentDir={sortDir} onSort={handleSort} />
                          <TableHead className="text-slate-400 font-medium w-[80px]">Indexed</TableHead>
                          <TableHead className="text-slate-400 font-medium w-[60px]">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {books.map((book) => (
                          <TableRow 
                            key={book.book_id} 
                            className="border-slate-700 hover:bg-slate-800/50 cursor-pointer"
                            onClick={() => handleViewBook(book)}
                            data-testid={`book-row-${book.book_id}`}
                          >
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded bg-slate-700 flex items-center justify-center flex-shrink-0">
                                  <BookOpen className="w-4 h-4 text-slate-400" />
                                </div>
                                <div className="min-w-0">
                                  <p className="font-medium text-white truncate max-w-[200px]" title={book.title}>
                                    {book.title}
                                  </p>
                                  <p className="text-xs text-slate-500">{book.age_days}d old</p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <User className="w-3 h-3 text-slate-500" />
                                <span className="text-slate-300 truncate max-w-[120px]" title={book.author}>
                                  {book.author}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              {book.publisher_name ? (
                                <div className="flex items-center gap-2">
                                  <Building2 className="w-3 h-3 text-slate-500" />
                                  <span className="text-slate-400 truncate max-w-[100px]" title={book.publisher_name}>
                                    {book.publisher_name}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-slate-500">Independent</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="text-slate-300">{formatNumber(book.total_readers)}</span>
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="text-amber-400 font-medium">{formatRC(book.rc_consumed)}</span>
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="text-slate-300">{formatSeconds(book.total_playback_seconds)}</span>
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="text-slate-300">{formatSeconds(book.avg_engagement_seconds)}</span>
                            </TableCell>
                            <TableCell className="text-slate-400">
                              {formatDate(book.last_activity_at)}
                            </TableCell>
                            <TableCell>
                              {book.seo_indexed ? (
                                <div className="flex items-center gap-1.5">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-900/50 text-emerald-400 border border-emerald-700/50">
                                    Indexed
                                  </span>
                                  <a
                                    href={`https://www.newaudia.com/book/${book.seo_slug}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-blue-400 hover:text-blue-300"
                                    data-testid={`seo-link-${book.book_id}`}
                                  >
                                    <ExternalLink className="w-3.5 h-3.5" />
                                  </a>
                                </div>
                              ) : (
                                <span className="text-slate-600">—</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => handleDeleteClick(e, book)}
                                className="text-slate-500 hover:text-red-500 hover:bg-red-500/10 p-2"
                                data-testid={`delete-book-${book.book_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-700">
                      <p className="text-sm text-slate-400">
                        Page {page} of {totalPages}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={page <= 1}
                          onClick={() => updateParams({ page: String(page - 1) })}
                          className="border-slate-500 text-white bg-slate-700 hover:bg-slate-600 hover:text-white disabled:opacity-40 disabled:text-slate-500"
                          data-testid="prev-page"
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!hasMore}
                          onClick={() => updateParams({ page: String(page + 1) })}
                          className="border-slate-500 text-white bg-slate-700 hover:bg-slate-600 hover:text-white disabled:opacity-40 disabled:text-slate-500"
                          data-testid="next-page"
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Period Footer */}
          {summary && (
            <div className="text-xs text-slate-500 text-center mt-4" data-testid="period-footer">
              Data period: {formatDate(summary.period_start)} – {formatDate(summary.period_end)}
            </div>
          )}
        </main>
      </div>

      {/* Book Analytics Drawer */}
      <BookAnalyticsDrawer
        book={selectedBook}
        isOpen={showDrawer}
        onClose={() => setShowDrawer(false)}
        dateFrom={dates.date_from}
        dateTo={dates.date_to}
        onViewBsp={(book) => {
          setBspModalBook(book);
          setShowBspModal(true);
        }}
      />

      {/* BSP Detail Modal */}
      <BSPDetailModal
        isOpen={showBspModal}
        onClose={() => setShowBspModal(false)}
        manuscriptId={bspModalBook?.book_id}
        bookTitle={bspModalBook?.title}
      />

      {/* Delete Book - Double Confirmation Modal */}
      <Dialog open={deleteStep > 0} onOpenChange={(open) => !open && handleDeleteCancel()}>
        <DialogContent className="bg-slate-900 border-red-500/30 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-500 flex items-center gap-2">
              {deleteStep === 1 ? (
                <>
                  <AlertTriangle className="w-5 h-5" />
                  Delete Book
                </>
              ) : (
                <>
                  <Trash2 className="w-5 h-5" />
                  FINAL WARNING - Permanent Deletion
                </>
              )}
            </DialogTitle>
            <div className="text-slate-300 text-sm mt-2">
              {deleteStep === 1 ? (
                <div className="space-y-3">
                  <span className="block">
                    You are about to delete <strong className="text-white">"{deleteTarget?.title}"</strong>.
                  </span>
                  <span className="block text-amber-400">
                    This will permanently remove the book and all associated data including:
                  </span>
                  <ul className="list-disc list-inside text-slate-400 space-y-1">
                    <li>All audio files</li>
                    <li>Reader progress and bookmarks</li>
                    <li>Analytics and engagement data</li>
                    <li>BSP and semantic profiles</li>
                  </ul>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4">
                    <span className="text-red-400 font-semibold">
                      ⚠️ THIS ACTION CANNOT BE UNDONE
                    </span>
                  </div>
                  <span className="block">
                    You are permanently deleting <strong className="text-white">"{deleteTarget?.title}"</strong>.
                  </span>
                  <span className="block text-slate-400">
                    All data will be removed from all libraries and cannot be recovered.
                  </span>
                </div>
              )}
            </div>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={handleDeleteCancel}
              className="bg-slate-800 border-slate-600 text-white hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className={deleteStep === 1 
                ? "bg-amber-600 hover:bg-amber-500 text-white" 
                : "bg-red-600 hover:bg-red-500 text-white"
              }
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : deleteStep === 1 ? (
                'Continue'
              ) : (
                'DELETE PERMANENTLY'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Success Modal */}
      <Dialog open={showDeleteSuccess} onOpenChange={setShowDeleteSuccess}>
        <DialogContent className="bg-slate-900 border-green-500/30 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-green-500 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Selected Title Has Been Removed
            </DialogTitle>
            <div className="text-slate-300 text-sm mt-2">
              <span className="block">
                <strong className="text-white">"{deletedTitle}"</strong> has been permanently deleted from all libraries.
              </span>
              <span className="block mt-2 text-slate-400">
                All associated data including audio files, reader progress, and analytics have been removed.
              </span>
            </div>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => setShowDeleteSuccess(false)}
              className="bg-green-600 hover:bg-green-500 text-white"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AdminMobileBottomNav />
      <UniversalFooter />
    </div>
  );
};

export default AdminBooksPage;
